// Il TICKET del socket e il TAVOLO DI PRATICA del bridge: la forma del corpo e della risposta del BFF di Limulo
// (K9b-1a). Funzioni PURE, niente rete: le due mutation stanno in `services/limuloApi.ts` (`issueBridgeTicket`, `createBridgePractice`).
//
// Forma COPIATA dalla fonte, non inventata: Qlash-IT/QBridge `limulo-app/` @ 643827d (0.2.0), `bff-endpoint-catalog.proposal.js`
// (le voci `bridge.session.ticket.issue` e `bridge.practice.create`) e `lib/rotte.js` (`corpoPratica`, `ticket`, `tavolo`); misurata sul
// BFF di staging il 09/10 12:20-12:22Z (Limulo a87da12, profilo di collaudo): la pratica rende 201 `{ tableId, commit, clientSeed, wsUrl }`,
// il ticket 201 `{ ticket, expiresInSeconds: 30, wsUrl }`. Il socket sta su un host SUO, diverso dal REST: l'indirizzo e `wsUrl`
// della risposta del ticket, mai scritto qui.
//
// Il ticket e MONOUSO e dura 30 s: si chiede a OGNI connessione (`lib/bridge/socket.ts`), mai da una cache, mai salvato.
// Il `clientSeed` e la parte del CLIENT nel provably-fair (ADR-0214, accepted): casuale, ≥128 bit, a ogni tavolo di pratica.

// ⚠️ NIENTE import qui: `tools/verify-limulo.ts` carica questo file con node (senza risolvere `./protocol`). I quattro posti, in senso orario da Nord (come `protocol.ts`), sono ripetuti.
const SEATS = ['N', 'E', 'S', 'W'] as const;
type Seat = (typeof SEATS)[number];

/** Le rotte del BFF. Due POST: non si ritentano mai da sole (un nuovo tentativo e un ticket, o un tavolo, NUOVO). */
export const BRIDGE_TICKET_ROUTE = '/v1/games/bridge/session-ticket';
export const BRIDGE_PRATICA_ROUTE = '/v1/games/bridge/practice-tables';
/** `bridge.table.mine.list` (K9b-1b-i, «Riprendi»): una GET, i tavoli ancora VIVI di chi chiama. Forma SERVITA (misurata 09/10 12:20:50Z): `{ provider, count, data: [{ tableId, game, seat, wsUrl }] }`. */
export const BRIDGE_TAVOLI_MIEI_ROUTE = '/v1/games/bridge/tables/mine';

/** L'`id` della rotta `bridge/tavolo/:id` e l'uuid del tavolo del game host (minuscolo o no: lo normalizza chi lo manda). */
const UUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
export const bridgeTableIdValido = (id: string | null | undefined): id is string => typeof id === 'string' && UUID.test(id);

/** Il game host accetta SOLO l'uuid minuscolo (contratto §4): si manda cosi, qualunque sia la grafia dell'URL. */
export const bridgeTicketBody = (tableId: string): { tableId: string } => ({ tableId: tableId.toLowerCase() });

export interface BridgeTicketGrant {
  ticket: string;
  wsUrl: string;
}
/** La risposta 2xx a cui manca qualcosa: `assente` dice QUALE dei due (il ticket vince), per la riga di diagnosi — mai il valore. */
export interface BridgeTicketAssente {
  assente: 'ticket' | 'wsUrl';
}

const asRecord = (v: unknown): Record<string, unknown> => (v !== null && typeof v === 'object' && !Array.isArray(v) ? (v as Record<string, unknown>) : {});
const testo = (v: unknown): string | null => (typeof v === 'string' && v.trim() !== '' ? v.trim() : null);

/** Un indirizzo di socket e un URL http(s)/ws(s) che si legge: `wsUrl: ""` e il caso di `PUBLIC_WS_URL` mancante sul game host. */
function indirizzoDelSocket(v: unknown): string | null {
  const s = testo(v);
  if (s === null) return null;
  try {
    return ['https:', 'http:', 'wss:', 'ws:'].includes(new URL(s).protocol) ? s : null;
  } catch {
    return null;
  }
}

/** Il ticket dal `data` del BFF; senza ticket o senza un indirizzo che si legge dice quale manca. */
export function bridgeTicketRispostaOf(data: unknown): BridgeTicketGrant | BridgeTicketAssente {
  const r = asRecord(data);
  const ticket = testo(r.ticket);
  if (ticket === null) return { assente: 'ticket' };
  const wsUrl = indirizzoDelSocket(r.wsUrl);
  if (wsUrl === null) return { assente: 'wsUrl' };
  return { ticket, wsUrl };
}
export const bridgeTicketAssente = (x: BridgeTicketGrant | BridgeTicketAssente): x is BridgeTicketAssente => 'assente' in x;

// ── il tavolo di pratica: l'utente a Sud, tre robot dello stesso livello
export const LIVELLI_PRATICA = ['base', 'intermedio', 'esperto'] as const;
export type LivelloPratica = (typeof LIVELLI_PRATICA)[number];
export const livelloValido = (v: unknown): v is LivelloPratica => typeof v === 'string' && (LIVELLI_PRATICA as readonly string[]).includes(v);

/** 32 byte = 256 bit (il minimo dichiarato e 128): esadecimale minuscolo, 64 caratteri (il game host ne accetta fino a 128). */
export const CLIENT_SEED_BYTE = 32;

/**
 * Il `clientSeed` di UN tavolo: `crypto.getRandomValues` e nient'altro (mai `Math.random`, mai l'ora). Senza `getRandomValues` rende `null`
 * e il tavolo NON si crea: un seme che non e del client non e la sua parte del provably-fair.
 */
export function nuovoClientSeed(): string | null {
  const c = (globalThis as { crypto?: Crypto }).crypto;
  if (!c || typeof c.getRandomValues !== 'function') return null;
  const byte = new Uint8Array(CLIENT_SEED_BYTE);
  c.getRandomValues(byte);
  return Array.from(byte, (b) => b.toString(16).padStart(2, '0')).join('');
}

export interface BridgePraticaScelte {
  livello: LivelloPratica;
  clientSeed: string;
}
export const bridgePraticaBody = (s: BridgePraticaScelte): { livello: LivelloPratica; clientSeed: string } => ({ livello: s.livello, clientSeed: s.clientSeed });

/** `ok: false` porta il NOME del campo della risposta che manca o non torna (mai il valore): la pagina lo scrive in una lista chiusa. */
export type BridgePraticaRisposta = { ok: true; tableId: string; commit: string | null } | { ok: false; campo: 'tableId' | 'clientSeed' };

/**
 * La risposta della creazione. Il `tableId` deve essere un uuid; il `clientSeed` che torna deve essere ESATTAMENTE quello mandato: se il
 * game host ne ha usato un altro (o nessuno) la parte del client nel provably-fair non e la nostra, e il tavolo non si apre.
 * Il `wsUrl` della risposta si IGNORA: l'indirizzo del socket e quello del ticket.
 */
export function bridgePraticaRispostaOf(data: unknown, clientSeedMandato: string): BridgePraticaRisposta {
  const r = asRecord(data);
  const tableId = testo(r.tableId);
  if (tableId === null || !bridgeTableIdValido(tableId)) return { ok: false, campo: 'tableId' };
  if (r.clientSeed !== clientSeedMandato) return { ok: false, campo: 'clientSeed' };
  return { ok: true, tableId: tableId.toLowerCase(), commit: testo(r.commit) };
}

// ── «Riprendi»: i miei tavoli vivi (`bridge.table.mine.list`). Forma COPIATA da QBridge `limulo-app/` (`lib/rotte.js`, `test/resolvers.test.js`):
// `{ tables: [{ tableId, game: 'bridge', seat, wsUrl }] }`. Il `wsUrl` si IGNORA (l'indirizzo del socket e quello del ticket); il game host ne tiene al
// massimo 3 per persona, con ~10' di vita.
export interface TavoloMio {
  tableId: string;
  /** Il posto a cui siedo, se il game host lo dice (`seat` in lettere, o `posto` 0..3) e lo conosciamo; altrimenti `null`. */
  seat: Seat | null;
}
export type BridgeTavoliMiei = { ok: true; tavoli: TavoloMio[] } | { ok: false };
/**
 * Il posto di una voce dell'elenco. Prima di M3 era `seat` in lettere (`N`…`W`); dal core comune e `posto` 0..3 (`GET /service/tables` → `{ tableId, posto, posti, attivo, wsUrl }`, QBridge @ 444c8ad,
 * `tests/fixtures/bridge-busta-core.json`): MAPPA FISSA 0=N 1=E 2=S 3=W. Se ci sono tutti e due vale la lettera; se non c'e ne uno che si legge, `null` (mai un errore: «Riprendi» riapre il tavolo comunque).
 */
const postoDi = (voce: Record<string, unknown>): TavoloMio['seat'] => {
  if (typeof voce.seat === 'string' && (SEATS as readonly string[]).includes(voce.seat)) return voce.seat as Seat;
  return typeof voce.posto === 'number' && Number.isInteger(voce.posto) ? (SEATS[voce.posto] ?? null) : null;
};
/**
 * Lettura CHIUSA: l'elenco e `data` (un array) e nient'altro. Una risposta senza di lui — anche con un altro campo elenco, come `tables` — e un
 * errore (`ok: false`), mai «nessun tavolo» e mai un ripiego su un campo che non e quello servito; un tavolo senza un uuid o di un altro gioco si salta.
 */
export function bridgeTavoliMieiOf(data: unknown): BridgeTavoliMiei {
  const lista = asRecord(data).data;
  if (!Array.isArray(lista)) return { ok: false };
  const tavoli: TavoloMio[] = [];
  for (const voce of lista) {
    const r = asRecord(voce);
    const tableId = testo(r.tableId);
    if (tableId === null || !bridgeTableIdValido(tableId)) continue;
    if (r.game !== undefined && r.game !== 'bridge') continue;
    tavoli.push({ tableId: tableId.toLowerCase(), seat: postoDi(r) });
  }
  return { ok: true, tavoli };
}
